import { Link, useLocation } from "react-router-dom";
import { Home, Compass, Radio, Zap, MonitorPlay, User, Bell, LogOut, LogIn, UserPlus, ShieldCheck, X, Mic } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useState, useRef, useEffect } from "react";
import { useNotifications, formatNotifTime } from "../hooks/useNotifications";

const SP_LOGO = "https://customer-assets.emergentagent.com/job_emt-task-check/artifacts/gaylzcdf_SP%20Logo%20Main.jpg";

const NAV_MAIN = [
  { label: "Feed", icon: Home, href: "/" },
  { label: "Explore", icon: Compass, href: "/explore" },
  { label: "Radio", icon: Radio, href: "/radio" },
  { label: "Live", icon: Zap, href: "/live" },
];

export default function Sidebar() {
  const { pathname } = useLocation();
  const { user, profile, role, logout, isCreator, isAdmin } = useAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const { notifications, unreadCount, markAllRead } = useNotifications(user?.uid);

  // Close notification panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  const isActive = (href) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 w-60 z-40"
      style={{ background: "#111111", borderRight: "1px solid rgba(255,255,255,0.04)" }}
    >
      {/* Logo */}
      <Link to="/" className="flex items-center gap-2.5 px-5 py-5">
        <img
          src={SP_LOGO}
          alt="StagePass"
          className="w-9 h-9 rounded-xl object-cover flex-shrink-0"
        />
        <span className="font-heading font-black text-white text-lg tracking-tight">STAGEPASS</span>
      </Link>

      {/* Main Nav */}
      <nav className="flex-1 px-3 pt-2 space-y-0.5 overflow-y-auto">
        {NAV_MAIN.map(({ label, icon: Icon, href }) => (
          <Link
            key={href}
            to={href}
            data-testid={`nav-${label.toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-150 ${
              isActive(href)
                ? "text-white font-semibold"
                : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
            style={isActive(href) ? { background: "#C026D3" } : {}}
          >
            <Icon size={17} />
            <span className="text-sm font-medium">{label}</span>
          </Link>
        ))}

        {/* Creator / Admin section */}
        {(isCreator || isAdmin || (user && !isCreator && !isAdmin)) && (
          <div className="pt-3 pb-1">
            <p className="text-zinc-600 text-[10px] font-semibold uppercase tracking-widest px-4 mb-1">
              {isAdmin ? "Admin" : isCreator ? "Creator" : "Create"}
            </p>
            {isCreator && !isAdmin && (
              <Link
                to="/dashboard"
                data-testid="nav-studio"
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-150 ${
                  isActive("/dashboard")
                    ? "text-white font-semibold"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
                style={isActive("/dashboard") ? { background: "#C026D3" } : {}}
              >
                <MonitorPlay size={17} />
                <span className="text-sm font-medium">Studio</span>
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-150 ${
                  isActive("/admin")
                    ? "text-white font-semibold"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
                style={isActive("/admin") ? { background: "#C026D3" } : {}}
              >
                <ShieldCheck size={17} />
                <span className="text-sm font-medium">Admin</span>
              </Link>
            )}
            {/* Apply as creator CTA for regular users */}
            {user && !isCreator && !isAdmin && (
              <Link
                to="/apply"
                data-testid="nav-apply"
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-150 ${
                  isActive("/apply")
                    ? "text-white font-semibold"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
                style={isActive("/apply") ? { background: "#C026D3" } : {}}
              >
                <Mic size={17} />
                <span className="text-sm font-medium">Become a Creator</span>
              </Link>
            )}
          </div>
        )}

        {user && (
          <div className="pt-1">
            <Link
              to={isCreator ? "/dashboard/profile" : "/apply"}
              data-testid="nav-profile"
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-150 ${
                isActive("/dashboard/profile")
                  ? "text-white font-semibold"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              style={isActive("/dashboard/profile") ? { background: "#C026D3" } : {}}
            >
              <User size={17} />
              <span className="text-sm font-medium">Profile</span>
            </Link>
          </div>
        )}
      </nav>

      {/* Bottom section */}
      <div className="px-3 pb-4 space-y-0.5 border-t border-white/5 pt-3">
        {user ? (
          <>
            {/* User info */}
            <div className="flex items-center gap-3 px-4 py-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-[#2a1a3e] overflow-hidden flex-shrink-0">
                {profile?.photoURL ? (
                  <img src={profile.photoURL} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#B026FF] text-xs font-bold">
                    {(profile?.displayName || profile?.email || "U")[0].toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-white text-xs font-medium truncate">{profile?.displayName || "User"}</p>
                <p className="text-zinc-500 text-[10px] capitalize truncate">{role}</p>
              </div>
            </div>

            {/* Notification bell with real data */}
            <div className="relative" ref={notifRef}>
              <button
                data-testid="notification-bell"
                onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen && unreadCount > 0) markAllRead(); }}
                className="relative flex items-center gap-3 px-4 py-2.5 rounded-xl w-full text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
              >
                <Bell size={17} />
                <span className="text-sm font-medium">Notifications</span>
                {unreadCount > 0 ? (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                ) : (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-zinc-700" />
                )}
              </button>

              {notifOpen && (
                <div
                  data-testid="notification-panel"
                  className="absolute bottom-full left-0 right-0 mb-2 rounded-xl shadow-2xl overflow-hidden z-50"
                  style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/5">
                    <p className="text-xs font-bold text-white">Notifications</p>
                    <button onClick={() => setNotifOpen(false)} className="text-zinc-600 hover:text-white">
                      <X size={12} />
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-zinc-600 text-xs text-center py-5">No notifications yet</p>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`px-3 py-2.5 border-b border-white/5 ${n.read ? "opacity-50" : ""}`}
                        >
                          <p className="text-white text-xs leading-relaxed">{n.message}</p>
                          <p className="text-zinc-600 text-[10px] mt-0.5">{formatNotifTime(n.createdAt)}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sign out */}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl w-full text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
            >
              <LogOut size={17} />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </>
        ) : (
          <>
            <Link
              to="/auth"
              data-testid="login-btn"
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-zinc-400 hover:text-white hover:bg-white/5 transition-all"
            >
              <LogIn size={17} />
              <span className="text-sm font-medium">Log In</span>
            </Link>
            <Link
              to="/auth?mode=register"
              data-testid="join-btn"
              className="flex items-center justify-center gap-2 mx-1 py-2.5 rounded-full font-bold text-sm transition-colors"
              style={{ background: "#00ff77", color: "#000" }}
            >
              <UserPlus size={14} />
              Join STAGEPASS
            </Link>
          </>
        )}
      </div>
    </aside>
  );
}
